/**
 * O que se digita, no idioma do jogo, virando o que o catálogo entende.
 *
 * As 1240 figuras têm nome em inglês, porque é assim que os dois pacotes de
 * origem as nomeiam. Sem isto, quem digita "águia" no jogo em português recebe
 * "Nada encontrado." num catálogo com dezenove águias, e a busca é o único
 * caminho até as mil e cem figuras.
 *
 * **Traduz o que se procura, e não o que existe.** Traduzir 1240 nomes para
 * três idiomas seriam 3720 linhas para manter, e a maior parte delas para
 * figuras que ninguém procura pelo nome. O que se digita é conceito, e conceito
 * de brasão de clube cabe em algumas centenas: bicho, arma, planta, corpo
 * celeste, peça de heráldica.
 *
 * **A chave é sempre a palavra já normalizada**, minúscula e sem acento, do
 * jeito que `normalizar` do catálogo devolve. Por isso `aguia` e não `águia`.
 *
 * Quem digita em inglês continua achando: a expansão acrescenta, nunca
 * substitui.
 */

/** Cada entrada é `palavra digitada: termos que o catálogo usa`. */
const SINONIMOS: Readonly<Record<string, readonly string[]>> = {
  // ---------------------------------------------------------------------
  // Bichos, que é o que mais se procura num escudo de clube
  // ---------------------------------------------------------------------
  aguia: ["eagle", "hawk", "falcon"],
  aguila: ["eagle", "hawk", "falcon"],
  leao: ["lion"],
  leon: ["lion"],
  lobo: ["wolf"],
  urso: ["bear"],
  touro: ["bull"],
  boi: ["bull", "ox"],
  toro: ["bull"],
  cavalo: ["horse"],
  caballo: ["horse"],
  tubarao: ["shark"],
  tiburon: ["shark"],
  falcao: ["falcon", "hawk"],
  gaviao: ["hawk", "falcon"],
  cobra: ["snake", "cobra", "serpent"],
  serpente: ["snake", "serpent"],
  serpiente: ["snake", "serpent"],
  tigre: ["tiger"],
  raposa: ["fox"],
  zorro: ["fox"],
  coruja: ["owl"],
  buho: ["owl"],
  corvo: ["raven", "crow"],
  cuervo: ["raven", "crow"],
  javali: ["boar"],
  veado: ["stag", "deer"],
  cervo: ["stag", "deer"],
  ciervo: ["stag", "deer"],
  dragao: ["dragon"],
  dragon: ["dragon"],
  grifo: ["griffin"],
  fenix: ["phoenix"],
  pantera: ["panther"],
  escorpiao: ["scorpion"],
  escorpion: ["scorpion"],
  aranha: ["spider"],
  arana: ["spider"],
  abelha: ["bee"],
  abeja: ["bee"],
  borboleta: ["butterfly"],
  mariposa: ["butterfly"],
  peixe: ["fish"],
  pez: ["fish"],
  baleia: ["whale"],
  ballena: ["whale"],
  golfinho: ["dolphin"],
  delfin: ["dolphin"],
  elefante: ["elephant"],
  rinoceronte: ["rhino"],
  gorila: ["gorilla", "ape"],
  macaco: ["monkey", "ape"],
  mono: ["monkey", "ape"],
  carneiro: ["ram"],
  cabra: ["goat"],
  bode: ["goat"],
  galo: ["rooster", "cock"],
  gallo: ["rooster", "cock"],
  pato: ["duck"],
  cisne: ["swan"],
  gaivota: ["seagull", "gull"],
  gaviota: ["seagull", "gull"],
  morcego: ["bat"],
  murcielago: ["bat"],
  gato: ["cat"],
  cachorro: ["dog", "hound"],
  cao: ["dog", "hound"],
  perro: ["dog", "hound"],
  pata: ["paw"],
  garra: ["claw", "talon"],
  asa: ["wing"],
  ala: ["wing"],
  pena: ["feather"],
  pluma: ["feather"],
  presa: ["fang"],
  chifre: ["horn"],
  cuerno: ["horn"],
  besouro: ["beetle"],
  caranguejo: ["crab"],
  cangrejo: ["crab"],
  polvo: ["octopus"],
  pulpo: ["octopus"],
  tartaruga: ["turtle"],
  tortuga: ["turtle"],
  sapo: ["frog"],
  rana: ["frog"],
  lagarto: ["lizard"],
  rato: ["rat", "mouse"],
  raton: ["rat", "mouse"],
  passaro: ["bird"],
  pajaro: ["bird"],
  ave: ["bird"],
  bicho: ["animal"],
  animal: ["animal"],

  // ---------------------------------------------------------------------
  // Heráldica
  // ---------------------------------------------------------------------
  coroa: ["crown"],
  corona: ["crown"],
  escudo: ["shield", "crest", "emblem"],
  brasao: ["crest", "emblem", "heraldic"],
  emblema: ["emblem", "crest"],
  bandeira: ["flag", "banner"],
  bandera: ["flag", "banner"],
  estandarte: ["banner"],
  louro: ["laurel"],
  laurel: ["laurel"],
  flor: ["flower", "fleur", "rose", "tulip"],
  rei: ["king"],
  rey: ["king"],
  rainha: ["queen"],
  reina: ["queen"],
  cavaleiro: ["knight"],
  caballero: ["knight"],
  castelo: ["castle", "fortress"],
  castillo: ["castle", "fortress"],
  torre: ["tower"],
  fortaleza: ["fortress", "castle"],
  portao: ["gate"],
  chave: ["key"],
  llave: ["key"],
  fita: ["ribbon"],
  medalha: ["medal"],
  medalla: ["medal"],
  selo: ["seal"],
  pergaminho: ["scroll"],
  trono: ["throne"],
  cetro: ["sceptre", "scepter"],

  // ---------------------------------------------------------------------
  // Armas
  // ---------------------------------------------------------------------
  espada: ["sword", "blade"],
  machado: ["axe"],
  hacha: ["axe"],
  lanca: ["spear", "lance"],
  lanza: ["spear", "lance"],
  flecha: ["arrow"],
  arco: ["bow"],
  martelo: ["hammer"],
  martillo: ["hammer"],
  tridente: ["trident"],
  adaga: ["dagger"],
  punhal: ["dagger"],
  elmo: ["helmet"],
  capacete: ["helmet"],
  casco: ["helmet"],
  armadura: ["armor", "armour"],
  canhao: ["cannon"],
  ancora: ["anchor"],
  ancla: ["anchor"],
  corrente: ["chain"],
  cadena: ["chain"],
  faca: ["knife", "blade"],
  cuchillo: ["knife", "blade"],
  besta: ["crossbow"],
  bomba: ["bomb"],

  // ---------------------------------------------------------------------
  // Natureza
  // ---------------------------------------------------------------------
  arvore: ["tree", "oak", "pine"],
  arbol: ["tree", "oak", "pine"],
  folha: ["leaf"],
  hoja: ["leaf"],
  montanha: ["mountain"],
  montana: ["mountain"],
  onda: ["wave"],
  ola: ["wave"],
  sol: ["sun", "sunrise", "sunset"],
  lua: ["moon"],
  luna: ["moon"],
  estrela: ["star"],
  estrella: ["star"],
  fogo: ["fire", "flame"],
  fuego: ["fire", "flame"],
  chama: ["flame", "fire"],
  llama: ["flame", "fire"],
  raio: ["lightning", "thunder", "bolt"],
  rayo: ["lightning", "thunder", "bolt"],
  trovao: ["thunder"],
  agua: ["water", "droplet"],
  nuvem: ["cloud"],
  nube: ["cloud"],
  chuva: ["rain"],
  lluvia: ["rain"],
  neve: ["snow", "snowflake"],
  nieve: ["snow", "snowflake"],
  vento: ["wind"],
  viento: ["wind"],
  vulcao: ["volcano"],
  volcan: ["volcano"],
  ilha: ["island"],
  isla: ["island"],
  pedra: ["rock", "stone"],
  piedra: ["rock", "stone"],
  cristal: ["crystal", "gem"],
  diamante: ["diamond", "gem"],
  joia: ["gem", "jewel"],
  carvalho: ["oak"],
  pinheiro: ["pine"],
  trevo: ["clover"],
  trebol: ["clover"],
  rosa: ["rose"],
  trigo: ["wheat", "grain"],
  floresta: ["forest", "tree"],
  bosque: ["forest", "tree"],
  rio: ["river"],
  mar: ["sea", "wave"],
  tempestade: ["storm"],
  tormenta: ["storm"],
  terra: ["earth"],
  tierra: ["earth"],

  // ---------------------------------------------------------------------
  // Esporte
  // ---------------------------------------------------------------------
  bola: ["ball", "soccer"],
  pelota: ["ball", "soccer"],
  futebol: ["soccer", "football", "ball"],
  futbol: ["soccer", "football", "ball"],
  chuteira: ["boot", "cleat"],
  bota: ["boot"],
  apito: ["whistle"],
  silbato: ["whistle"],
  trofeu: ["trophy", "cup"],
  trofeo: ["trophy", "cup"],
  taca: ["trophy", "cup"],
  copa: ["cup", "trophy"],
  gol: ["goal", "net"],
  estadio: ["stadium"],
  camisa: ["jersey", "shirt"],
  camiseta: ["jersey", "shirt"],
  luva: ["glove"],
  guante: ["glove"],
  rede: ["net"],
  campo: ["field", "pitch"],
  cancha: ["field", "pitch"],
  podio: ["podium"],

  // ---------------------------------------------------------------------
  // Máquina e objeto
  // ---------------------------------------------------------------------
  engrenagem: ["gear", "cog"],
  engranaje: ["gear", "cog"],
  bigorna: ["anvil"],
  yunque: ["anvil"],
  fabrica: ["factory"],
  motor: ["engine", "turbine"],
  chavedefenda: ["wrench"],
  parafuso: ["screw", "bolt"],
  cano: ["pipe"],
  roda: ["wheel"],
  rueda: ["wheel"],
  trem: ["train"],
  tren: ["train"],
  navio: ["ship", "boat", "sailboat"],
  barco: ["ship", "boat", "sailboat"],
  aviao: ["plane"],
  avion: ["plane"],
  foguete: ["rocket"],
  cohete: ["rocket"],

  // ---------------------------------------------------------------------
  // Formas e símbolos
  // ---------------------------------------------------------------------
  circulo: ["circle"],
  quadrado: ["square"],
  cuadrado: ["square"],
  triangulo: ["triangle"],
  losango: ["diamond", "rhombus"],
  rombo: ["diamond", "rhombus"],
  cruz: ["cross"],
  espiral: ["spiral"],
  infinito: ["infinity"],
  gota: ["drop", "droplet"],
  olho: ["eye"],
  ojo: ["eye"],
  mao: ["hand"],
  mano: ["hand"],
  punho: ["fist", "hand"],
  coracao: ["heart"],
  corazon: ["heart"],
  caveira: ["skull"],
  cranio: ["skull"],
  calavera: ["skull"],
  craneo: ["skull"],
  osso: ["bone"],
  hueso: ["bone"],
  relogio: ["clock", "hourglass", "timer"],
  reloj: ["clock", "hourglass", "timer"],
  ampulheta: ["hourglass"],
  seta: ["arrow"],
  flecha2: ["arrow"],
  anel: ["ring"],
  anillo: ["ring"],
  piramide: ["pyramid"],
  livro: ["book"],
  libro: ["book"],
  musica: ["music"],
  pincel: ["brush"],
  tenda: ["tent"],
  casa: ["home", "house"],
};

/**
 * O que procurar de fato, a partir do que a pessoa digitou.
 *
 * Devolve sempre a palavra original junto, senão quem digita em inglês, que é
 * o idioma dos nomes, deixaria de achar.
 */
export function termosDeBusca(palavra: string): string[] {
  const traducoes = SINONIMOS[palavra];
  return traducoes ? [palavra, ...traducoes] : [palavra];
}
